import React, { useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useForm, Controller, FormProvider } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { Link, useNavigate, useParams } from 'react-router-dom'

import DropDown from 'components/Input/DropDown'
import ErrorMessageWithExclamation from 'components/UI/ErrorMessageWithExclamation'
import { Input, Checkbox, Textarea, Button } from 'components/UI'
import WithUploadBtn from '../../../../../../../components/pages/Register/CreateBanners/components/Step1/WithUploadBtn'
import Radio from '../../../../../../../components/UI/Radio'

import { selectRole } from '../../../../../../../routing/fetchingfunction/user/user.reducer'
import {
  selectAddJobError,
  selectAddJobLoading,
  selectEditJobError,
  selectEditJobLoading,
  selectJob,
} from '../../../../../../../routing/fetchingfunction/job/job.reducer'
import {
  addJob,
  editJob,
  getJob,
  resetJob,
} from '../../../../../../../routing/fetchingfunction/job/job.actions'

import { useAvailableCategories } from '../../../../../../../hooks'

import { CreateJobPostScheme } from '../../../../../../../routing/utils/validators'

import { handleNumberInputWheel } from '../../../../../../../helpers'

import { priceTypes } from '../../../../../../../constants'
import { MAX_UPLOADED_IMAGES } from 'constants/upload'

const JobsPostsAdd = ({ className = '' }) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const params = useParams()

  const role = useSelector(selectRole)
  const isLoading = useSelector(selectAddJobLoading)
  const editIsLoading = useSelector(selectEditJobLoading)
  const error = useSelector(selectAddJobError)
  const editError = useSelector(selectEditJobError)
  const job = useSelector(selectJob)

  const methods = useForm({
    defaultValues: {
      job_category: '',
      is_public: false,
      price: '',
      price_type: '',
      job_title: '',
      post_title: '',
      description: '',
      keywords: '',
      images: [],
      crop: false,
    },
    resolver: yupResolver(CreateJobPostScheme),
  })

  const {
    formState: { errors },
    setValue,
    register,
    handleSubmit,
    control,
    watch,
  } = methods

  const { formattedPassedCategories } = useAvailableCategories('passed')

  const { description, keywords } = watch()

  const [originalImages, setOriginalImages] = useState(false)

  const handleEdit = (data) => {
    try {
      const formData = new FormData()

      const jobPostData = {}

      for (let key in data) {
        if (key === 'images') {
          continue
        }

        jobPostData[key] = data[key]
      }

      const newImages = []
      const deletedImages = []
      let newImagesStartIndex = 0

      data.images.forEach((image) => {
        if (image.size) {
          newImages.push(image)
        } else if (image.original) {
          const deletedIndex = originalImages.findIndex(
            (originalImage) => originalImage.original === image.original
          )

          deletedImages.push(deletedIndex)
        }
      })

      newImagesStartIndex = originalImages.length - deletedImages.length

      if (newImagesStartIndex < 0) {
        newImagesStartIndex = 0
      }
      console.log({ newImagesStartIndex, originalImages, deletedImages })

      newImages.forEach((image) => {
        newImagesStartIndex += 1

        formData.append(`picture_${newImagesStartIndex}`, image)
      })

      jobPostData.images = data.images
        .map((image, index) => {
          if (!deletedImages.find((deleteImageIndex) => deleteImageIndex === index)) {
            return index + 1
          }

          return null
        })
        .filter(Boolean)
        .map((image) => {
          return `picture${image}`
        })

      formData.append('job', JSON.stringify(jobPostData))

      dispatch(
        editJob({
          id: job.job_hash,
          data: formData,
        })
      ).then((data) => {
        if (!data.error) {
          navigate(`/${role}/settings/my-banners/jobs-posts`)
        }
      })
    } catch (e) {
      console.error(e)
    }
  }

  const handleFormSubmit = (data) => {
    try {
      if (job) {
        return handleEdit(data)
      }

      const formData = new FormData()

      const jobData = {}

      for (let key in data) {
        if (key === 'images') {
          continue
        }

        jobData[key] = data[key]
      }

      formData.append('job', JSON.stringify(jobData))

      data.images.forEach((image, index) => {
        formData.append(`picture_${index + 1}`, image)
      })

      dispatch(addJob(formData)).then((data) => {
        if (!data.error) {
          navigate(`/${role}/settings/my-banners/jobs-posts`)
        }
      })
    } catch (e) {
      console.error(e)
    }
  }

  useEffect(() => {
    if (job) {
      setValue('job_category', job.job_category.id || '')
      setValue('is_public', job.is_public || false)
      setValue('price', job.price || '')
      setValue('price_type', job.price_type || '')
      setValue('job_title', job.job_title || '')
      setValue('post_title', job.post_title || '')
      setValue('description', job.description || '')
      setValue('keywords', job.keywords || '')
      setValue('crop', job.crop || false)
      setValue('images', job.images || [])

      setOriginalImages(job.images)
    }
  }, [job])

  useEffect(() => {
    if (params.id) {
      dispatch(getJob(params.id))
    }
  }, [params])

  useEffect(() => {
    return () => {
      dispatch(resetJob())
    }
  }, [])

  const priceTypesContent = priceTypes.map((priceType) => {
    return (
      <Radio
        key={priceType.value}
        variant="secondary"
        wrapperClassName="hover:cursor-pointer"
        labelClassName="text-sm font-medium text-primary-100"
        label={priceType.label}
        value={priceType.value}
        {...register('price_type')}
      />
    )
  })

  return (
    <div className={`mx-auto w-full flex flex-col items-center max-w-[800px]`}>
      <div
        className={`bg-[#FEEBDD] rounded-lg text-[#F67443] max-w-[800px] w-full px-[148px] py-[64px] flex flex-col items-center justify-start`}
      >
        <div className={`w-full pt-6 ${className}`}>
          <h1 className="text-3xl text-center w-full font-semibold mb-[24px]">
            {job ? 'Edit Job Post' : 'Create New Job Post'}
          </h1>

          {(typeof error === 'string' || typeof editError === 'string') && (
            <ErrorMessageWithExclamation text={error || editError} className="mb-4" scroll />
          )}

          <FormProvider {...methods}>
            <form className="w-full" onSubmit={handleSubmit(handleFormSubmit)}>
              <Controller
                control={control}
                name="is_public"
                render={({ field: { value, onChange } }) => (
                  <Checkbox
                    label="Publish"
                    wrapperClassName="mb-4"
                    checked={value}
                    onChange={onChange}
                  />
                )}
              />

              <div className="mb-3">
                <label className="text-sm mb-1">Category</label>

                <Controller
                  control={control}
                  name="job_category"
                  render={({ field: { onChange, value } }) => (
                    <DropDown
                      menuItems={formattedPassedCategories}
                      setValue={onChange}
                      placeholder="Select Category"
                      value={value}
                    />
                  )}
                />

                {errors.job_category && (
                  <ErrorMessageWithExclamation text={errors.job_category.message} />
                )}
              </div>

              <div className="mb-3">
                <label className="text-sm mb-1">Job Title</label>

                <Controller
                  control={control}
                  name="job_title"
                  render={({ field }) => (
                    <Input
                      wrapperClassName="w-full"
                      placeholder="Enter your job title (max. 100 characters)"
                      type="text"
                      {...field}
                    />
                  )}
                />

                {errors.job_title ? (
                  <ErrorMessageWithExclamation text={errors.job_title.message} />
                ) : (
                  <p className="mt-[8px] text-xs text-primary-button">
                    This outlines your job qualifications
                  </p>
                )}
              </div>

              <div className="mb-3">
                <label className="text-sm mb-1">Post Title</label>

                <Controller
                  control={control}
                  name="post_title"
                  render={({ field }) => (
                    <Input
                      wrapperClassName="w-full"
                      placeholder="Enter post title (max. 100 characters)"
                      type="text"
                      {...field}
                    />
                  )}
                />

                {errors.post_title ? (
                  <ErrorMessageWithExclamation text={errors.post_title.message} />
                ) : (
                  <p className="mt-[8px] text-xs text-primary-button">
                    This serves as the header for your post
                  </p>
                )}
              </div>

              <div className="mb-[10px]">
                <label className="text-sm mb-1">Price</label>
                <Controller
                  control={control}
                  name="price"
                  render={({ field }) => (
                    <Input
                      wrapperClassName="w-full"
                      placeholder="0"
                      leftAddon="$"
                      type="number"
                      className="without-arrows"
                      onWheel={handleNumberInputWheel}
                      {...field}
                    />
                  )}
                />

                {errors.price && <ErrorMessageWithExclamation text={errors.price.message} />}
              </div>

              <div className="mb-[20px]">
                <div className="w-full flex items-center gap-x-[16px]">{priceTypesContent}</div>

                {errors.price_type && (
                  <ErrorMessageWithExclamation text={errors.price_type.message} />
                )}
              </div>

              <div className="mb-3 relative">
                <span className="absolute top-0 right-0">{description.length}/3000</span>

                <label className="text-sm mb-1">Description</label>

                <Controller
                  control={control}
                  name="description"
                  render={({ field }) => (
                    <Textarea rows={4} placeholder="Enter a description" {...field} />
                  )}
                />

                {errors.description && (
                  <ErrorMessageWithExclamation text={errors.description.message} />
                )}
              </div>

              <div className="mb-3 relative">
                <span className="absolute top-0 right-0">{keywords.length}/150</span>

                <label className="text-sm mb-1">Keywords</label>

                <Controller
                  control={control}
                  name="keywords"
                  render={({ field }) => <Textarea rows={4} placeholder="Keywords" {...field} />}
                />

                {errors.keywords ? (
                  <ErrorMessageWithExclamation text={errors.keywords.message} />
                ) : (
                  <p className="text-xs text-[#F79009] mt-1">
                    Define keywords separated by commas to help students find your job post.
                    Keywords are hidden.
                  </p>
                )}
              </div>

              <div className="mb-[6px]">
                <Controller
                  control={control}
                  name="images"
                  render={({ field }) => (
                    <>
                      <label className="text-sm mb-1">
                        Image ({field.value.length}/{MAX_UPLOADED_IMAGES})
                      </label>
                      <WithUploadBtn type="images" {...field} />
                    </>
                  )}
                />

                {errors.images && <ErrorMessageWithExclamation text={errors.images.message} />}
              </div>

              <Controller
                control={control}
                name="crop"
                render={({ field: { value, onChange } }) => (
                  <Checkbox
                    label="Crop and centre you face"
                    wrapperClassName="mb-[24px]"
                    checked={value}
                    onChange={onChange}
                  />
                )}
              />

              <Button
                className="w-full h-[44px] font-semibold"
                type="submit"
                isLoading={isLoading || editIsLoading}
              >
                {job ? 'Edit' : 'Create'} Job Banner
              </Button>

              <div className="text-center mt-[16px]">
                <Link
                  to={`/${role}/settings/my-banners/jobs-posts`}
                  className="bg-transparent !text-[#F79009] font-bold"
                >
                  Cancel
                </Link>
              </div>
            </form>
          </FormProvider>
        </div>
      </div>
    </div>
  )
}

export default JobsPostsAdd
